import React from 'react';
import { Column } from 'react-table';
import { formatDate, getYear } from '../../../../common/helpers/dateHelper';
import { SortType } from '../../../../common/enums/SortType';
import { FormattedMessage } from 'react-intl';
import CustomBadge from '../../../../components/CustomBadge';
import { ICandidateScore } from '../../../../common/models/candidate/ICandidateScore';
import IconButton from '../../../../components/IconButton';
import Icon from '../../../../components/Icon';
import { IBindingCallback1 } from '../../../../common/models/callback';
import { CandidateStatus } from '../../../../common/enums/CandidateStatus';
import { inactiveProgressMessage } from '../../../../common/helpers/dashboardHelper';
import ScoreStar from '../../../../components/ScoreStar';
import TextOverflowTooltip from '../../../../components/TextOverflowTooltip';
import EmptyFieldPlaceholder from '../../../../components/EmptyFieldPlaceholder';

export const getColumns = (onSurveyClick: IBindingCallback1<string>): Column[] => [
  {
    Header: () => <FormattedMessage id="date" defaultMessage="Date" />,
    accessor: item => {
      const { date } = item as ICandidateScore;
      return date ? formatDate(date) : '';
    },
    id: 'date',
    sortType: SortType.Date,
    width: 120
  },
  {
    Header: () => <FormattedMessage id="candidateTable.candidateName" defaultMessage="Candidate name" />,
    id: 'name',
    Cell: ({ row: { original } }) => {
      const { firstName, lastName } = original as ICandidateScore;
      return (
        <TextOverflowTooltip content={`${lastName}, ${firstName}`} width={150}>
          <div className="cell-active">
            {`${lastName}, ${firstName}`}
          </div>
        </TextOverflowTooltip>
      );
    },
    sortType: SortType.Numeric
  },
  {
    Header: () => <FormattedMessage id="candidateTable.refRelationship" defaultMessage="Relationship to candidate" />,
    id: 'relationship',
    Cell: ({ row: { original } }) => {
      const { referenceType, workEndDate, workStartDate } = original as ICandidateScore;

      if (!workStartDate) {
        return <EmptyFieldPlaceholder />;
      }

      const startYear = `‘${getYear(workStartDate)}`;
      const endYear = workEndDate
        ? `‘${getYear(workEndDate)}`
        : <FormattedMessage id="present" defaultMessage="Present" />;
      return (
        <div>
          {referenceType}
          &nbsp;
          (
          {startYear}
          -
          {endYear}
          )
        </div>
      );
    },
    sortType: SortType.Literal,
    width: 200
  },
  {
    Header: () => <FormattedMessage id="contactProfile.company" defaultMessage="Company" />,
    id: 'workInCompany',
    Cell: ({ row: { original } }) => {
      const { workInCompany } = original as ICandidateScore;
      return workInCompany
        ? (
          <TextOverflowTooltip content={workInCompany} width={145}>
            <span>{workInCompany}</span>
          </TextOverflowTooltip>
        )
        : <EmptyFieldPlaceholder />;
    },
    sortType: SortType.Literal
  },
  {
    Header: () => <FormattedMessage id="contactProfile.candidateStatus" defaultMessage="Candidate status" />,
    id: 'status',
    Cell: ({ row: { original } }) => {
      const { candidateStatus } = original as ICandidateScore;
      const statusLabel = candidateStatus === CandidateStatus.Active
        ? <FormattedMessage id="active" defaultMessage="Active" />
        : inactiveProgressMessage(candidateStatus);

      return <CustomBadge status={candidateStatus}>{statusLabel}</CustomBadge>;
    },
    sortType: SortType.Numeric
  },
  {
    Header: () => <FormattedMessage id="card.survey.title" defaultMessage="Survey" />,
    id: 'survey',
    Cell: ({ row: { original } }) => {
      const { surveyId, surveyName } = original as ICandidateScore;
      return (
        <div className="table-center" style={{ maxWidth: '45px' }}>
          <IconButton
            onClick={() => onSurveyClick(surveyId)}
            tooltip={surveyName}
            icon={<Icon icon="survey" />}
            variant="blue"
            size="lg"
          />
        </div>
      );
    },
    width: 80
  },
  {
    Header: () => <FormattedMessage id="contactProfile.score" defaultMessage="Score" />,
    accessor: 'score',
    Cell: ({ cell: { value } }) => (
      <div className="table-center">
        <ScoreStar score={value} />
      </div>
    ),
    width: 80,
    sortType: SortType.Numeric
  }
];
